import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertTriangle, CheckCircle, Crown } from 'lucide-react';
import AppShell from '@/components/AppShell';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SubscriptionManagement = () => {
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSubscription();
  }, []);

  const fetchSubscription = async () => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/payments/subscription-status`, {
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      setSubscription(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
      toast.error('Failed to load subscription details');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    setIsCancelling(true);
    try {
      const sessionToken = localStorage.getItem('session_token');
      await axios.post(
        `${API}/payments/cancel-subscription`,
        {},
        { headers: { 'Authorization': `Bearer ${sessionToken}` } }
      );
      
      toast.success('Subscription cancelled. Access continues until end of billing period.');
      setShowCancelModal(false);
      fetchSubscription();
    } catch (error) {
      console.error('Cancellation failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    } finally {
      setIsCancelling(false);
    }
  };

  if (loading) {
    return (
      <AppShell>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-navy-900 border-r-transparent"></div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="max-w-4xl mx-auto p-4 md:p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold mb-2" style={{ fontFamily: 'Manrope', color: 'var(--navy-900)' }}>
              Subscription Management
            </h1>
            <p className="text-gray-600">Manage your BragBox subscription</p>
          </div>
        </div>

        {/* Subscription Card */}
        <div className="bg-white p-8 rounded-2xl border-2" style={{ borderColor: subscription?.status === 'cancelled' ? '#EF4444' : 'var(--navy-900)' }}>
          {/* Current Plan */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              {subscription?.plan === 'lifetime' && <Crown className="w-8 h-8" style={{ color: '#F59E0B' }} />}
              <div>
                <h2 className="text-2xl font-bold" style={{ fontFamily: 'Manrope', color: 'var(--navy-900)' }}>
                  {subscription?.plan === 'monthly' && 'Monthly Pro'}
                  {subscription?.plan === 'yearly' && 'Yearly Pro'}
                  {subscription?.plan === 'lifetime' && 'Lifetime Access'}
                  {(!subscription?.plan || subscription?.plan === 'free') && 'Free Plan'}
                </h2>
                <p className="text-sm text-gray-600">
                  {subscription?.status === 'trial' && 'Free Trial Active'}
                  {subscription?.status === 'active' && 'Active Subscription'}
                  {subscription?.status === 'cancelled' && 'Cancelled (Access until end of period)'}
                  {(subscription?.status === 'free' || !subscription?.status) && 'Free Plan'}
                </p>
              </div>
            </div>

            {/* Billing Info - ONLY for actual paid plans */}
            {(subscription?.plan === 'monthly' || subscription?.plan === 'yearly') && (
              <div className="space-y-3 p-6 rounded-lg" style={{ backgroundColor: 'var(--cream-50)' }}>
                <div className="flex justify-between">
                  <span className="text-sm font-medium">Amount:</span>
                  <span className="text-sm">
                    {subscription?.plan === 'monthly' ? '$14.99/month' : '$149/year'}
                  </span>
                </div>
                {subscription?.trial_end_date && (
                  <div className="flex justify-between">
                    <span className="text-sm font-medium">Trial ends:</span>
                    <span className="text-sm">
                      {new Date(subscription.trial_end_date).toLocaleDateString()}
                    </span>
                  </div>
                )}
                {subscription?.next_billing_date && (
                  <div className="flex justify-between">
                    <span className="text-sm font-medium">Next billing:</span>
                    <span className="text-sm">
                      {new Date(subscription.next_billing_date).toLocaleDateString()}
                    </span>
                  </div>
                )}
              </div>
            )}

            {subscription?.plan === 'lifetime' && (
              <div className="p-6 rounded-lg" style={{ backgroundColor: 'var(--cream-50)' }}>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" style={{ color: '#10B981' }} />
                  <span className="text-sm font-medium">Lifetime access - no recurring fees</span>
                </div>
              </div>
            )}
          </div>

          {/* Cancel - ONLY for actual paid plans */}
          {(subscription?.plan === 'monthly' || subscription?.plan === 'yearly') && subscription?.status !== 'cancelled' && (
            <div className="border-t pt-6">
              <h3 className="font-semibold mb-4" style={{ color: 'var(--navy-900)' }}>Actions</h3>
              <Button
                onClick={() => setShowCancelModal(true)}
                variant="outline"
                className="text-red-600 border-red-300 hover:bg-red-50"
              >
                Cancel Subscription
              </Button>
            </div>
          )}

          {subscription?.status === 'cancelled' && (
            <div className="border-t pt-6">
              <Button
                onClick={() => navigate('/pricing')}
                className="w-full py-6 rounded-xl"
                style={{ backgroundColor: 'var(--navy-900)', color: 'white' }}
              >
                Reactivate Subscription
              </Button>
            </div>
          )}

          {(!subscription?.plan || subscription?.plan === 'free') && subscription?.status !== 'cancelled' && (
            <div className="border-t pt-6">
              <Button
                onClick={() => navigate('/pricing')}
                className="w-full py-6 rounded-xl font-semibold"
                style={{ backgroundColor: 'var(--navy-900)', color: 'white' }}
                data-testid="sub-upgrade-btn"
              >
                Upgrade
              </Button>
            </div>
          )}
        </div>

        {/* Cancel Confirmation Modal */}
        <Dialog open={showCancelModal} onOpenChange={setShowCancelModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-2xl flex items-center gap-2">
                <AlertTriangle className="w-6 h-6 text-yellow-600" />
                Cancel Subscription?
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <p className="text-gray-700">
                You'll lose access to these features at the end of your billing period:
              </p>
              <ul className="space-y-2 pl-4">
                <li className="text-sm text-gray-600">• AI win enhancement</li>
                <li className="text-sm text-gray-600">• Connect wins to your actual goals automatically</li>
                <li className="text-sm text-gray-600">• Walk into reviews with a ready-to-share summary</li>
                <li className="text-sm text-gray-600">• Get nudged before you forget that win</li>
                <li className="text-sm text-gray-600">• Monthly motivation reports</li>
              </ul>

              <div className="flex gap-3 pt-4">
                <Button
                  onClick={handleCancelSubscription}
                  disabled={isCancelling}
                  variant="outline"
                  className="flex-1 text-red-600 border-red-300"
                >
                  {isCancelling ? 'Cancelling...' : 'Cancel Anyway'}
                </Button>
                <Button
                  onClick={() => setShowCancelModal(false)}
                  className="flex-1"
                  style={{ backgroundColor: 'var(--navy-900)', color: 'white' }}
                >
                  Keep Subscription
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AppShell>
  );
};

export default SubscriptionManagement;
